import {
  BodyText,
  Button,
  Checkbox,
  Layout,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  SelectMenu,
  toast,
} from '@postscript/components';
import { FlowTemplateEvents } from 'components/flowBuilder/constants/trackingEvents';
import { sampleSize, shuffle, uniqBy } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { logEvent, MODAL_TYPES, PRODUCT_AREAS } from 'utils/events';
import useResourceTemplates, {
  isResourceTemplate,
  ResourceTemplate,
  RESOURCE_TEMPLATE_TYPES,
  useDeleteFavoriteTemplate,
  useSaveFavoriteTemplate,
} from '../../hooks/useResourceTemplates';
import MessageTemplateCard from './MessageTemplateCard';
import {
  addColorMappings,
  filterTemplates,
  mapPreviousTemplates,
} from './MessageTemplateModalUtils';
import useModalTracking from './useModalTracking';

export interface MessageTemplateData {
  mediaUrl: string | null;
  text: string;
}

const isMessageTemplateData = (
  templateData: unknown,
): templateData is MessageTemplateData => {
  const templateDataValue = templateData as MessageTemplateData;
  if (!templateDataValue || typeof templateDataValue !== 'object') return false;
  if (
    typeof templateDataValue.mediaUrl !== 'string' &&
    templateDataValue.mediaUrl !== null
  )
    return false;
  if (!templateDataValue.text || typeof templateDataValue.text !== 'string')
    return false;
  return true;
};

export type MessageTemplate = ResourceTemplate<MessageTemplateData>;

export const isMessageTemplate = (
  template: unknown,
): template is MessageTemplate => {
  const templateValue = template as MessageTemplate;
  if (!isResourceTemplate(templateValue)) return false;
  if (!isMessageTemplateData(templateValue.data)) return false;
  return true;
};

interface SelectOption {
  label: string;
  value: string;
}

interface Props {
  isOpen: boolean;
  isShowByDefaultCheckboxVisible: boolean;
  onCancel: () => void;
  onSubmit: (messageTemplate: MessageTemplate) => void;
  displayContext: string;
}

const TemplateContainer = styled.div`
  height: 500px;
  max-height: 500px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-x-small);
  background-color: var(--main-bkg-color);
  overflow-y: auto;
  padding: var(--spacing-2);
  margin-top: var(--spacing-3);
`;

const MasonryLayout = styled.div`
  column-count: 2;
  column-gap: var(--spacing-2);
  break-inside: avoid;

  & > * {
    break-inside: avoid;
    margin-bottom: var(--spacing-2);
  }
`;

export interface MessageCardData extends MessageTemplate {
  color: string;
}

export const ALL_TEMPLATES_FILTER = 'All';
export const FAVORITE_TEMPLATES_FILTER = 'Favorites';

const createOption = (templateType: string): SelectOption => ({
  label: templateType,
  value: templateType,
});

const groupTemplatesByCategory = (
  data: MessageCardData[],
): { [category: string]: MessageCardData[] } =>
  data.reduce<{ [category: string]: MessageCardData[] }>(
    (previous, current) => ({
      ...previous,
      [current.category]: [
        ...(previous[current.category] ?? []),
        { ...current },
      ],
    }),
    {},
  );

const getFilterOptions = (data: MessageCardData[]): SelectOption[] => {
  const uniqueCategories = uniqBy(data, 'category');
  return [
    createOption(ALL_TEMPLATES_FILTER),
    createOption(FAVORITE_TEMPLATES_FILTER),
    ...uniqueCategories.map((c) => createOption(c.category)),
  ];
};

const randomizeTemplates = (data: MessageCardData[]): MessageCardData[] => {
  const groupedTemplates = groupTemplatesByCategory(data);
  const sampledTemplates = Object.keys(groupedTemplates).flatMap((key) =>
    sampleSize(groupedTemplates[key], 3),
  );

  return shuffle(sampledTemplates);
};

const deselectTemplateWhenFilteredOutOfView = (
  filteredTemplates: MessageCardData[],
  selectedTemplate: MessageTemplate | null,
  setSelectedTemplate: (template: null) => void,
) => {
  if (!filteredTemplates.some((t) => t.id === selectedTemplate?.id)) {
    setSelectedTemplate(null);
  }
};

const MessageTemplatesModal = ({
  isOpen,
  isShowByDefaultCheckboxVisible = true,
  onCancel,
  onSubmit,
  displayContext,
}: Props): JSX.Element => {
  const {
    data = [],
    isLoading,
    isSuccess,
  } = useResourceTemplates<MessageTemplateData>(
    RESOURCE_TEMPLATE_TYPES.CAMPAIGN,
    { enabled: isOpen },
  );
  const { mutateAsync: saveFavoriteMutate, isSuccess: deleteFavoriteSuccess } =
    useSaveFavoriteTemplate('campaign');
  const { mutateAsync: deleteFavoriteMutate, isSuccess: saveFavoriteSuccess } =
    useDeleteFavoriteTemplate('campaign');
  const isFavoriteUpdate = deleteFavoriteSuccess || saveFavoriteSuccess;

  const savedShowMessageTemplateModalForNewCampaignsValue =
    localStorage.getItem('isMessageTemplateModalEnabled') ?? 'true';
  const initialShowMessageTemplateValue =
    savedShowMessageTemplateModalForNewCampaignsValue === 'true';
  const [
    showMessageTemplateForNewCampaigns,
    setShowMessageTemplateForNewCampaigns,
  ] = useState(initialShowMessageTemplateValue);

  const [selectedTemplate, setSelectedTemplate] =
    useState<MessageTemplate | null>(null);
  const templates = useMemo(() => addColorMappings(data), [data]);
  const [selectedFilter, setSelectedFilter] = useState(ALL_TEMPLATES_FILTER);
  const filterOptions = getFilterOptions(templates);
  // Keep track of previous randomized templates between updates
  const randomizedTemplates = useRef<MessageCardData[]>(
    [] as MessageCardData[],
  );
  const messageTemplates = useMemo(() => {
    randomizedTemplates.current = randomizeTemplates(templates);
    return randomizedTemplates.current;
  }, [isSuccess]);
  const filteredTemplates = filterTemplates(
    selectedFilter,
    templates,
    isFavoriteUpdate
      ? mapPreviousTemplates(randomizedTemplates.current, templates)
      : messageTemplates,
  );
  const trackModalClose = useModalTracking({
    isOpen,
    modalType: MODAL_TYPES.TEMPLATES,
    productArea: PRODUCT_AREAS.CAMPAIGNS,
  });

  const logFilterEvent = (category: string) => {
    logEvent(FlowTemplateEvents.TEMPLATE_FILTERED, {
      template_category: category,
      product_area: displayContext,
    });
  };

  const logSubmitEvent = (selectedTemplate: MessageTemplate) => {
    logEvent(FlowTemplateEvents.TEMPLATE_SELECTED, {
      template_id: selectedTemplate?.id,
      template_type: selectedTemplate?.type,
      template_name: selectedTemplate?.name,
      template_category: selectedTemplate?.category,
      product_area: displayContext,
    });
  };

  const logFavoriteUnfavorite = (action: string, category: string) => {
    logEvent(action, {
      template_category: category,
      product_area: displayContext,
    });
  };

  const setFavoriteTemplate = async (messageTemplate: MessageTemplate) => {
    const { favorite: isDeleteFavorite, category, id } = messageTemplate;
    try {
      if (isDeleteFavorite) {
        logFavoriteUnfavorite(
          FlowTemplateEvents.TEMPLATE_UNFAVORITED,
          category,
        );
        await deleteFavoriteMutate(id);
        toast.success('Removed favorited template');
      } else {
        logFavoriteUnfavorite(FlowTemplateEvents.TEMPLATE_FAVORITED, category);
        await saveFavoriteMutate(id);
        toast.success('Favorited template');
      }
    } catch (error: unknown) {
      if (error instanceof Error) {
        toast.error(error.message);
      }
    }
  };

  const cancelModal = () => {
    trackModalClose();
    onCancel();
  };

  useEffect(() => {
    deselectTemplateWhenFilteredOutOfView(
      filteredTemplates,
      selectedTemplate,
      setSelectedTemplate,
    );
  }, [filteredTemplates, selectedTemplate]);

  return (
    <Modal
      isOpen={isOpen}
      onCancel={cancelModal}
      data-cy="message-templates-modal"
    >
      <ModalHeader onCancel={cancelModal}>Message templates</ModalHeader>
      <ModalBody>
        <BodyText id="message-template-header">
          Select an SMS template to use for your campaign.
        </BodyText>
        <SelectMenu
          label="Select a Template Type"
          options={filterOptions}
          isClearable={false}
          value={createOption(selectedFilter)}
          onChange={(option) => {
            logFilterEvent((option as SelectOption).value);
            setSelectedFilter((option as SelectOption).value);
          }}
          data-cy="template-type"
        />
        <TemplateContainer
          role="radiogroup"
          aria-labelledby="message-template-header"
        >
          <MasonryLayout>
            {filteredTemplates.map((messageTemplate, currentIndex) => {
              const isActive = messageTemplate.id === selectedTemplate?.id;
              return (
                <MessageTemplateCard
                  key={messageTemplate.id}
                  isActive={isActive}
                  messageTemplate={messageTemplate}
                  nextTemplate={filteredTemplates[currentIndex + 1]}
                  previousTemplate={filteredTemplates[currentIndex - 1]}
                  selectedTemplate={selectedTemplate}
                  setSelectedTemplate={setSelectedTemplate}
                  setFavoriteTemplate={setFavoriteTemplate}
                />
              );
            })}
          </MasonryLayout>
        </TemplateContainer>
      </ModalBody>
      <ModalFooter>
        <Layout
          align={isShowByDefaultCheckboxVisible ? 'justify' : 'right'}
          style={{ flex: 1 }}
        >
          {isShowByDefaultCheckboxVisible && (
            <Checkbox
              fieldSize="small"
              id="show-message-templates-modal-for-new-campaign"
              label="Always show for new campaigns"
              name="show-message-templates-modal-for-new-campaign"
              value="show-message-templates-modal-for-new-campaign"
              onChange={({ target: { checked } }) => {
                localStorage.setItem(
                  'isMessageTemplateModalEnabled',
                  checked ? 'true' : 'false',
                );
                setShowMessageTemplateForNewCampaigns(checked);
              }}
              checked={showMessageTemplateForNewCampaigns}
            />
          )}

          <Layout>
            <Button
              data-testid="cancel-button"
              variant="text"
              onClick={cancelModal}
            >
              Close
            </Button>
            <Button
              variant="primary"
              disabled={isLoading || !selectedTemplate}
              onClick={() => {
                logSubmitEvent(selectedTemplate as MessageTemplate);
                onSubmit(selectedTemplate as MessageTemplate);
              }}
              data-testid="confirm-button"
            >
              Confirm
            </Button>
          </Layout>
        </Layout>
      </ModalFooter>
    </Modal>
  );
};

export default MessageTemplatesModal;
